import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { TopBar } from "../components/Navigation";
import { resolveImageUrl } from "../components/ImageUploader";
import {
    ArrowLeft, BadgeCheck, ExternalLink, Globe, MapPin,
    CalendarDays, Instagram, Youtube, Mountain, Tag,
} from "lucide-react";

const CATEGORY_LABEL = {
    "outdoor-gear": "Outdoor Gear",
    apparel: "Apparel",
    footwear: "Footwear",
    nutrition: "Nutrition",
    electronics: "Electronics",
    travel: "Travel",
    other: "Brand",
};

const prettyUrl = (u) => (u || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
const yearOf = (iso) => { try { return new Date(iso).getFullYear(); } catch { return ""; } };

export default function BrandDetail() {
    const { user_id } = useParams();
    const [brand, setBrand] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/brands/${user_id}`);
                if (!cancelled) setBrand(data);
            } catch {
                if (!cancelled) setNotFound(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [user_id]);

    if (loading) {
        return (
            <div className="min-h-screen bg-alpine-mist">
                <TopBar title="Brand" />
                <div className="max-w-3xl mx-auto px-4 py-10">
                    <div className="h-48 bg-snowcap rounded-2xl animate-pulse mb-6" />
                    <div className="h-6 w-1/2 bg-snowcap rounded animate-pulse" />
                </div>
            </div>
        );
    }

    if (notFound || !brand) {
        return (
            <div className="min-h-screen bg-alpine-mist">
                <TopBar title="Brand" />
                <div className="max-w-3xl mx-auto px-4 py-16 text-center">
                    <Mountain className="w-10 h-10 text-slate-gray mx-auto mb-3" />
                    <h1 className="font-display font-black text-2xl text-midnight-pine">Brand not found</h1>
                    <p className="text-slate-gray mt-2">This partner may have left or isn't active.</p>
                    <Link to="/brands" className="inline-flex items-center gap-2 mt-5 text-ember-orange font-bold">
                        <ArrowLeft className="w-4 h-4" /> Back to all brands
                    </Link>
                </div>
            </div>
        );
    }

    const since = yearOf(brand.partner_since);
    const category = CATEGORY_LABEL[brand.category] || "Brand";
    const hero = resolveImageUrl(brand.hero_image_url);
    const logo = resolveImageUrl(brand.logo_url);
    const gear = brand.gear_highlights || [];

    return (
        <div data-testid="brand-detail" className="min-h-screen bg-alpine-mist">
            <TopBar title={brand.name} />
            <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
                <Link
                    to="/brands"
                    data-testid="brand-back-link"
                    className="inline-flex items-center gap-2 text-sm font-bold text-slate-gray hover:text-granite mb-4"
                >
                    <ArrowLeft className="w-4 h-4" /> All brands
                </Link>

                {/* Hero banner */}
                <div className="relative rounded-2xl overflow-hidden border border-ridge-line">
                    {hero ? (
                        <img src={hero} alt="" className="w-full h-40 sm:h-60 object-cover" />
                    ) : (
                        <div className="w-full h-32 sm:h-44 bg-gradient-to-br from-midnight-pine to-deep-evergreen" />
                    )}
                </div>

                {/* Identity card overlapping the hero */}
                <div className="relative -mt-12 mx-3 sm:mx-5 bg-snowcap border border-ridge-line rounded-xl shadow-lg p-5">
                    <div className="flex items-start gap-4">
                        <div className="w-20 h-20 rounded-xl border-2 border-snowcap bg-alpine-mist overflow-hidden shrink-0 -mt-10 shadow-md">
                            {logo ? (
                                <img src={logo} alt={brand.name} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <BadgeCheck className="w-8 h-8 text-slate-gray" />
                                </div>
                            )}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 data-testid="brand-name" className="font-display font-black text-2xl text-midnight-pine leading-tight">
                                    {brand.name}
                                </h1>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-moss/15 text-moss text-[10px] font-black uppercase tracking-widest">
                                    <BadgeCheck className="w-3 h-3" /> Verified partner
                                </span>
                            </div>
                            {brand.tagline && (
                                <p data-testid="brand-tagline" className="text-sm text-granite mt-1">{brand.tagline}</p>
                            )}
                        </div>
                    </div>

                    {/* Meta chips */}
                    <div className="flex flex-wrap gap-2 mt-4">
                        <MetaChip icon={Tag} label={category} />
                        {brand.location && <MetaChip icon={MapPin} label={brand.location} />}
                        {brand.founded_year && <MetaChip icon={CalendarDays} label={`Est. ${brand.founded_year}`} />}
                        {since && <MetaChip icon={BadgeCheck} label={`Partner since ${since}`} />}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-2 mt-4">
                        {brand.website && (
                            <a
                                data-testid="brand-website-btn"
                                href={brand.website}
                                target="_blank"
                                rel="noopener noreferrer nofollow"
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-ember-orange text-white text-sm font-bold hover:bg-ember-orange/90 transition-all"
                            >
                                <Globe className="w-4 h-4" /> Visit {prettyUrl(brand.website)}
                                <ExternalLink className="w-3.5 h-3.5 opacity-80" />
                            </a>
                        )}
                        {brand.instagram && (
                            <SocialBtn testid="brand-instagram" href={brand.instagram} icon={Instagram} />
                        )}
                        {brand.youtube && (
                            <SocialBtn testid="brand-youtube" href={brand.youtube} icon={Youtube} />
                        )}
                    </div>
                </div>

                {/* About */}
                {brand.description ? (
                    <section data-testid="brand-about" className="mt-6 bg-snowcap border border-ridge-line rounded-xl p-5">
                        <h2 className="font-display font-black text-lg text-midnight-pine mb-2">About {brand.name}</h2>
                        <p className="text-[15px] leading-relaxed text-granite whitespace-pre-line">{brand.description}</p>
                    </section>
                ) : (
                    <p className="mt-6 text-center text-sm text-slate-gray">This partner hasn't added their story yet.</p>
                )}

                {/* Gear highlights */}
                {gear.length > 0 && (
                    <section className="mt-6 bg-snowcap border border-ridge-line rounded-xl p-5">
                        <h2 className="font-display font-black text-lg text-midnight-pine mb-3">What they're known for</h2>
                        <div className="flex flex-wrap gap-2">
                            {gear.map((g, i) => (
                                <span
                                    key={i}
                                    data-testid={`brand-gear-${i}`}
                                    className="px-3 py-1.5 rounded-full bg-trail-tan/60 text-midnight-pine text-sm font-bold"
                                >
                                    {g}
                                </span>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}

const MetaChip = ({ icon: Icon, label }) => (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-alpine-mist text-granite text-xs font-bold">
        <Icon className="w-3.5 h-3.5 text-slate-gray" /> {label}
    </span>
);

const SocialBtn = ({ testid, href, icon: Icon }) => (
    <a
        data-testid={testid}
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-ridge-line text-granite hover:border-ember-orange hover:text-ember-orange transition-all"
    >
        <Icon className="w-4 h-4" />
    </a>
);
